import PaginatedTable from '../../components/ui/PaginatedTable';
import PermissionButton from "../../components/ui/PermissionButton";
import TableActions from '../../components/ui/TableActions';
import { useEffect, useState } from "react";
import DashboardLayout from "../Layouts/DashboardLayout";
import DepartmentSelect from '../../components/ui/DepartmentSelect';

const API = "/api";

const EMPTY_FORM = {
  branch_id: "",
  department_id:'',
  counter_name: "",
  counter_code: "",
  is_active: true,
};

export default function Counters() {
  const [counters, setCounters] = useState([]);
  const [branches, setBranches] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);

  const fetchCounters = async () => {
    const res = await fetch(`${API}/counters/`, {
      credentials: "include",
    });

    const data = await res.json();

    if (data.status === "success") {
      setCounters(data.data);
    } else {
      alert(data.message || "Could not load counters");
    }
  };

  const fetchBranches = async () => {
    const res = await fetch(`${API}/branches`, {
      credentials: "include",
    });

    const data = await res.json();

    if (data.status === "success") {
      setBranches(data.data);
    }
  };

  useEffect(() => {
    fetchCounters();
    fetchBranches();
  }, []);

  const resetForm = () => {
    setForm(EMPTY_FORM);
    setEditing(null);
  };

  const openAdd = () => {
    resetForm();
    setShowModal(true);
  };

  const openEdit = (counter) => {
    setEditing(counter.id);
    setForm({
      branch_id: counter.branch_id,
      department_id:counter.department_id || '',
      counter_name: counter.counter_name,
      counter_code: counter.counter_code,
      is_active: counter.is_active,
    });
    setShowModal(true);
  };

  const saveCounter = async () => {
    if (
      !form.branch_id ||
      !form.department_id ||
      !form.counter_name.trim() ||
      !form.counter_code.trim()
    ) {
      alert("Branch, counter name, and counter code are required");
      return;
    }

    const res = await fetch(
      editing ? `${API}/counters/${editing}` : `${API}/counters/`,
      {
        method: editing ? "PUT" : "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          ...form,
          branch_id: Number(form.branch_id),
          department_id:Number(form.department_id),
          counter_name: form.counter_name.trim(),
          counter_code: form.counter_code.trim().toUpperCase(),
        }),
      },
    );

    const data = await res.json();

    if (data.status !== "success") {
      alert(data.message || "Could not save counter");
      return;
    }

    await fetchCounters();
    setShowModal(false);
    resetForm();
  };

  const deleteCounter = async (id) => {
    if (!window.confirm("Delete this counter?")) return;

    const res = await fetch(`${API}/counters/${id}`, {
      method: "DELETE",
      credentials: "include",
    });

    const data = await res.json();

    if (data.status !== "success") {
      alert(data.message || "Could not delete counter");
      return;
    }

    await fetchCounters();
  };

  return (
    <DashboardLayout>
      <div className="space-y-10">
        <div className="flex items-center justify-between">
          <h1 className="text-2xl font-serif">Counters</h1>

          <PermissionButton permissionModule="counters"
            onClick={openAdd}
            className="rounded-xl bg-amber-400 px-5 py-2 font-medium text-black"
          >
            + Add Counter
          </PermissionButton>
        </div>

        <div className="overflow-hidden rounded-2xl border border-white/10 bg-white/5">
          <PaginatedTable className="w-full text-sm">
            <thead className="bg-black/40 text-gray-400">
              <tr>
                <th className="px-6 py-4 text-left">Counter</th>
                <th className="px-6 py-4 text-left">Code</th>
                <th className="px-6 py-4 text-left">Branch</th>
                <th className="px-6 py-4 text-left">Status</th>
                <th className="px-6 py-4 text-center">Actions</th>
              </tr>
            </thead>

            <tbody>
              {counters.length === 0 ? (
                <tr>
                  <td colSpan="5" className="p-8 text-center text-gray-400">
                    No counters found
                  </td>
                </tr>
              ) : (
                counters.map((counter) => (
                  <tr
                    key={counter.id}
                    className="border-t border-white/10 hover:bg-white/5"
                  >
                    <td className="px-6 py-4 font-medium">
                      {counter.counter_name}
                      <div className="mt-1 text-xs text-gray-400">{counter.department || 'Sales department not assigned'}</div>
                    </td>
                    <td className="px-6 py-4">{counter.counter_code}</td>
                    <td className="px-6 py-4">{counter.branch_name}</td>
                    <td className="px-6 py-4">
                      <span
                        className={`rounded-full px-3 py-1 text-xs ${
                          counter.is_active
                            ? "bg-emerald-500/20 text-emerald-400"
                            : "bg-red-500/20 text-red-400"
                        }`}
                      >
                        {counter.is_active ? "Active" : "Inactive"}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-center"><TableActions>
                      <div className="flex justify-center gap-2">
                        <PermissionButton permissionModule="counters" permissionAction="edit"
                          onClick={() => openEdit(counter)}
                          className="rounded-lg border border-yellow-400 px-3 py-1 text-xs text-yellow-400"
                        >
                          Edit
                        </PermissionButton>
                        <PermissionButton permissionModule="counters" permissionAction="delete"
                          onClick={() => deleteCounter(counter.id)}
                          className="rounded-lg border border-red-500 px-3 py-1 text-xs text-red-400"
                        >
                          Delete
                        </PermissionButton>
                      </div>
                    </TableActions></td>
                  </tr>
                ))
              )}
            </tbody>
          </PaginatedTable>
        </div>

        {showModal && (
          <Modal
            onClose={() => {
              setShowModal(false);
              resetForm();
            }}
          >
            <h2 className="mb-6 text-xl font-serif">
              {editing ? "Edit Counter" : "Add Counter"}
            </h2>

            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <DepartmentSelect value={form.department_id} onChange={value=>setForm({...form,department_id:value})} branchId={form.branch_id} module="sales" label="Counter Sales department" />
              <select
                value={form.branch_id}
                onChange={(e) =>
                  setForm({ ...form, branch_id: e.target.value })
                }
                className="rounded-lg border border-white/10 bg-black/40 px-4 py-2"
              >
                <option value="">Select Branch</option>
                {branches.map((branch) => (
                  <option key={branch.id} value={branch.id}>
                    {branch.name}
                  </option>
                ))}
              </select>

              <input
                placeholder="Counter Name (e.g. Counter 1)"
                value={form.counter_name}
                onChange={(e) =>
                  setForm({ ...form, counter_name: e.target.value })
                }
                className="rounded-lg border border-white/10 bg-black/40 px-4 py-2"
              />

              <input
                placeholder="Counter Code (e.g. JSC1)"
                value={form.counter_code}
                onChange={(e) =>
                  setForm({
                    ...form,
                    counter_code: e.target.value.toUpperCase(),
                  })
                }
                className="rounded-lg border border-white/10 bg-black/40 px-4 py-2"
              />

              <select
                value={form.is_active ? "true" : "false"}
                onChange={(e) =>
                  setForm({
                    ...form,
                    is_active: e.target.value === "true",
                  })
                }
                className="rounded-lg border border-white/10 bg-black/40 px-4 py-2"
              >
                <option value="true">Active</option>
                <option value="false">Inactive</option>
              </select>
            </div>

            <div className="mt-8 flex justify-end gap-4">
              <PermissionButton permissionModule="counters"
                onClick={() => {
                  setShowModal(false);
                  resetForm();
                }}
                className="rounded-xl border border-white/20 px-6 py-2"
              >
                Cancel
              </PermissionButton>

              <PermissionButton permissionModule="counters"
                onClick={saveCounter}
                className="rounded-xl bg-amber-400 px-6 py-2 font-medium text-black"
              >
                {editing ? "Update Counter" : "Save Counter"}
              </PermissionButton>
            </div>
          </Modal>
        )}
      </div>
    </DashboardLayout>
  );
}

function Modal({ children, onClose }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
      <div className="relative w-full max-w-3xl rounded-2xl border border-white/10 bg-[#0b0b0f] p-8">
        <PermissionButton permissionModule="counters"
          onClick={onClose}
          className="absolute right-4 top-4 text-gray-400 hover:text-white"
        >
          ✕
        </PermissionButton>
        {children}
      </div>
    </div>
  );
}
